'use client';

import { Box, VStack, HStack } from '@terra/ui-styled-system';
import {
  Badge,
  BodyDefault,
  BodyStrong,
  HeadingItem,
  LabelDefault,
  MetaDefault,
  MonoDefault,
  MonoSmall,
  Tabs,
  Scroll,
  Tooltip,
  MarkdownPreview,
  ConfirmationDialog,
  toasterApi,
  BadgeHttp,
  EndpointFeatureTagBadges,
  CopyIdButton,
  EndpointsWebpagesTab,
  ASSET_TYPE_VALUES,
  WebSocketSchemaSection,
  ArchivedLifecycleBadge,
  isArchivedLifecycle,
} from '@terra/ui-design-system';
import { LIST_BADGE_LIFECYCLE_STATE } from '@terra/shared-dto-types';
import { useMentions } from '../../hooks/useMentions';
import type { EndpointReturnDto } from '@terra/shared-contracts-endpoints';
import type { EndpointSourceType } from '@terra/shared-schemas-orchestrator';
import { Link02Icon, TerminalIcon } from '@terra/ui-icons';
import { AssetDetailsHeaderMutations } from '../common/AssetDetailsHeaderMutations';
import React, { useCallback, useState } from 'react';
import { useTranslations } from 'next-intl';
import { normalizeHttpHeaders } from '@terra/shared-utils';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { terraProxyApiClient } from '../../clients/testerClient';
import { useInvalidateEndpoints } from '../../hooks/api/useEndpoints';
import type { EndpointMainContentTab } from '../../store/layoutStore';
import {
  useEndpointTabs,
  useBottomPanel,
  useMainContentTabs,
} from '../../store/layoutStore';
import {
  useAppTargetId,
  useTargetId,
  useTargetType,
} from '../../store/projectStore';
import { useApplicationCredentials } from '../../hooks/useApplicationCredentials';
import { useSignalCounts } from '../../hooks/useSignalCounts';
import { extractEndpointIdFromTab } from '../../utils/extractEndpointIdFromTab';
import { TabScopeProvider, useTabScope } from '../../contexts/TabScopeContext';
import { RequestsContent } from './RequestsContent';
import { SignalsContent } from './SignalsContent';
import { FindingsContent } from './FindingsContent';
import { TracesContent } from './TracesContent';
import { CodeModule } from '../CodeModule';
import { EndpointMemoriesContent } from './EndpointMemoriesContent';
import { useGlobalContextMenu } from '../../hooks/useGlobalContextMenu';
import { GlobalMenuItemsTypeEnum } from '../../store/globalContextMenuStore';
import type { CellContextMenuEvent } from 'ag-grid-community';
import { ENDPOINT_SOURCE_TRANSLATION_KEY_BY_VALUE } from './endpoint-source-labels';

const ENTITY_TAB_TYPE = {
  ENDPOINT: 'endpoint',
  WEBPAGE: 'webpage',
} as const;

interface EndpointDetailsTabProps {
  tab: EndpointMainContentTab;
}
interface EndpointDisplayData {
  method: string;
  path: string;
  url: string;
  description: string | null;
  status: number;
  credentialsIds?: string[];
  credentialRoles?: string[];
  sources?: string[];
  lifecycle?: EndpointReturnDto['lifecycle'];
}
const safeGetHostname = (url?: string, path?: string): string => {
  const fallback = 'api.example.com';

  // Try to extract hostname from URL first
  if (url) {
    try {
      return new URL(url).hostname;
    } catch (error) {
      console.warn('Invalid URL for endpoint:', url, error);
    }
  }

  // Try to extract hostname from path if it looks like a full URL
  if (path && path.includes('://')) {
    try {
      return new URL(path).hostname;
    } catch {
      // If that fails too, just use the fallback
    }
  }

  return fallback;
};

// Section header component
function SectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <LabelDefault
      color="fg.muted"
      textTransform="uppercase"
      letterSpacing="wide"
      mb={2}
    >
      {children}
    </LabelDefault>
  );
}

// Metadata item row
function MetadataItem({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <HStack gap="2" alignItems="flex-start" fontSize="sm">
      <MetaDefault color="fg.muted" minW="100px" flexShrink={0}>
        {label}:
      </MetaDefault>
      <Box flex="1">{children}</Box>
    </HStack>
  );
}

// Header row component
function HeaderRow({ headerKey, value }: { headerKey: string; value: string }) {
  return (
    <HStack
      gap="4"
      py="1.5"
      borderBottomWidth="1px"
      borderColor="border.subtle"
      _last={{ borderBottomWidth: 0 }}
    >
      <MonoSmall color="fg.muted" minW="180px" flexShrink={0}>
        {headerKey}
      </MonoSmall>
      <MonoSmall color="fg.default" wordBreak="break-all" flex="1">
        {value}
      </MonoSmall>
    </HStack>
  );
}

// Format body schema for display
function formatBodySchema(bodySchema: string | null | undefined): {
  content: string;
  language: string;
} {
  if (!bodySchema) return { content: '', language: 'json' };

  try {
    const parsed = JSON.parse(bodySchema);

    // Handle GraphQL queries
    if (parsed.documentString) {
      return { content: parsed.documentString, language: 'graphql' };
    }

    return { content: JSON.stringify(parsed, null, 2), language: 'json' };
  } catch {
    return { content: bodySchema, language: 'text' };
  }
}

// Extracted component for Details tab content
function EndpointDetailsContent({
  endpointData,
}: {
  endpointData: EndpointReturnDto;
}) {
  const [selectedResponseIndex, setSelectedResponseIndex] = React.useState(0);
  const request = endpointData.request;
  const responses = endpointData.responses || [];
  const selectedResponse = responses[selectedResponseIndex] || responses[0];
  const requestBodyInfo = React.useMemo(
    () => formatBodySchema(request?.bodySchema),
    [request?.bodySchema],
  );
  const responseBodyInfo = React.useMemo(
    () => formatBodySchema(selectedResponse?.bodySchema),
    [selectedResponse?.bodySchema],
  );

  return (
    <Scroll orientation="vertical" showScrollbar={true} h="full">
      <VStack gap="6" p="4" alignItems="stretch">
        <Box>
          <SectionHeader>Request</SectionHeader>
          <VStack
            gap="3"
            alignItems="stretch"
            p="3"
            bg="bg.subtle"
            borderRadius="md"
            borderWidth="1px"
            borderColor="border.muted"
          >
            <MetadataItem label="Method">
              <BadgeHttp method={request?.method || 'GET'} size="sm">
                {request?.method || 'GET'}
              </BadgeHttp>
            </MetadataItem>

            {request?.bodyType && (
              <MetadataItem label="Body Type">
                <Badge size="sm" variant="outline">
                  {request.bodyType}
                </Badge>
              </MetadataItem>
            )}

            {request?.pathParams && request.pathParams.length > 0 && (
              <MetadataItem label="Path Params">
                <HStack gap="1" flexWrap="wrap">
                  {request.pathParams.map((param, i) => (
                    <Badge key={i} size="sm" variant="emphasis">
                      :{param}
                    </Badge>
                  ))}
                </HStack>
              </MetadataItem>
            )}

            {request?.searchParams && request.searchParams.length > 0 && (
              <MetadataItem label="Query Params">
                <HStack gap="1" flexWrap="wrap">
                  {request.searchParams.map((param, i) => (
                    <Tooltip key={i} title={`${param.key}=${param.value}`}>
                      <Badge size="sm" variant="emphasis">
                        {param.key}
                      </Badge>
                    </Tooltip>
                  ))}
                </HStack>
              </MetadataItem>
            )}

            {request?.headers && request.headers.length > 0 && (
              <Box w="full">
                <MetaDefault color="fg.muted" mb={2}>
                  Headers:
                </MetaDefault>
                <Box
                  bg="bg.default"
                  borderRadius="md"
                  borderWidth="1px"
                  borderColor="border.muted"
                  px="3"
                  py="1"
                >
                  {normalizeHttpHeaders(
                    request.headers.map((header) => ({
                      name: header.key,
                      value: header.value,
                    })),
                  ).map((header, i) => (
                    <HeaderRow
                      key={i}
                      headerKey={header.name}
                      value={header.value}
                    />
                  ))}
                </Box>
              </Box>
            )}

            {request?.cookies && request.cookies.length > 0 && (
              <MetadataItem label="Cookies">
                <HStack gap="1" flexWrap="wrap">
                  {request.cookies.map((cookie, i) => (
                    <Tooltip key={i} title={`${cookie.name}=${cookie.value}`}>
                      <Badge size="sm" variant="emphasis">
                        {cookie.name}
                      </Badge>
                    </Tooltip>
                  ))}
                </HStack>
              </MetadataItem>
            )}

            {requestBodyInfo.content && (
              <Box w="full">
                <MetaDefault color="fg.muted" mb={2}>
                  Body Schema:
                </MetaDefault>
                <Box borderRadius="md" overflow="auto" maxH="250px" w="full">
                  <MarkdownPreview
                    source={`\`\`\`${requestBodyInfo.language}\n${requestBodyInfo.content}\n\`\`\``}
                    compact
                  />
                </Box>
              </Box>
            )}
          </VStack>
        </Box>

        {/* RESPONSES SECTION */}
        <Box>
          <HStack gap="2" mb="2" alignItems="center">
            <SectionHeader>Responses</SectionHeader>
            {responses.length > 1 && (
              <HStack gap="1">
                {responses.map((resp, index) => (
                  <Badge
                    key={index}
                    size="sm"
                    variant={
                      index === selectedResponseIndex ? 'fill' : 'outline'
                    }
                    cursor="pointer"
                    onClick={() => setSelectedResponseIndex(index)}
                    _hover={{ opacity: 0.8 }}
                  >
                    {resp.status || 200}
                  </Badge>
                ))}
              </HStack>
            )}
          </HStack>

          {selectedResponse && (
            <VStack
              gap="3"
              alignItems="stretch"
              p="3"
              bg="bg.subtle"
              borderRadius="md"
              borderWidth="1px"
              borderColor="border.muted"
            >
              <MetadataItem label="Status">
                <Badge size="sm" variant="fill">
                  {selectedResponse.status || 200}
                </Badge>
              </MetadataItem>

              {selectedResponse.bodyType && (
                <MetadataItem label="Body Type">
                  <Badge size="sm" variant="outline">
                    {selectedResponse.bodyType}
                  </Badge>
                </MetadataItem>
              )}

              {selectedResponse.headers &&
                selectedResponse.headers.length > 0 && (
                  <Box w="full">
                    <MetaDefault color="fg.muted" mb={2}>
                      Headers:
                    </MetaDefault>
                    <Box
                      bg="bg.default"
                      borderRadius="md"
                      borderWidth="1px"
                      borderColor="border.muted"
                      px="3"
                      py="1"
                    >
                      {normalizeHttpHeaders(
                        selectedResponse.headers.map((header) => ({
                          name: header.key,
                          value: header.value,
                        })),
                      ).map((header, i) => (
                        <HeaderRow
                          key={i}
                          headerKey={header.name}
                          value={header.value}
                        />
                      ))}
                    </Box>
                  </Box>
                )}

              {selectedResponse.cookies &&
                selectedResponse.cookies.length > 0 && (
                  <MetadataItem label="Set-Cookie">
                    <HStack gap="1" flexWrap="wrap">
                      {selectedResponse.cookies.map((cookie, i) => (
                        <Tooltip
                          key={i}
                          title={`${cookie.name}=${cookie.value}`}
                        >
                          <Badge size="sm" variant="emphasis">
                            {cookie.name}
                          </Badge>
                        </Tooltip>
                      ))}
                    </HStack>
                  </MetadataItem>
                )}

              {responseBodyInfo.content && (
                <Box w="full">
                  <MetaDefault color="fg.muted" mb={2}>
                    Response Schema:
                  </MetaDefault>
                  <Box borderRadius="md" overflow="auto" maxH="250px" w="full">
                    <MarkdownPreview
                      source={`\`\`\`${responseBodyInfo.language}\n${responseBodyInfo.content}\n\`\`\``}
                      compact
                    />
                  </Box>
                </Box>
              )}
            </VStack>
          )}
        </Box>

        <WebSocketSchemaSection data={endpointData} />
      </VStack>
    </Scroll>
  );
}

export function EndpointDetailsTab({ tab }: EndpointDetailsTabProps) {
  const appTargetId = useAppTargetId();
  const targetId = useTargetId();
  const targetType = useTargetType();
  const { addMention } = useMentions();
  const tEndpointMemories = useTranslations('memories.endpointMemories');
  const tEndpointDetails = useTranslations('proxy.endpointDetails');
  const tAssetEntity = useTranslations('designSystem.signals.drawer');
  const tLayout = useTranslations('layout');
  const { credentials } = useApplicationCredentials({
    applicationId: appTargetId,
    enabled: !!appTargetId,
  });
  const credentialsById = React.useMemo(() => {
    return new Map(
      credentials.map((credential) => [credential.id, credential]),
    );
  }, [credentials]);
  // Extract basic info from tab for use in error states
  const basicInfo = React.useMemo(() => {
    const fallbackPath = tab.title.replace(
      /^(GET|POST|PUT|DELETE|PATCH)\s+/,
      '',
    );
    const fallbackMethod = tab.title.split(' ')[0] || 'GET';

    return { method: fallbackMethod, path: fallbackPath };
  }, [tab.title]);
  const endpointId = React.useMemo(
    () => extractEndpointIdFromTab({ tab }),
    [tab],
  );
  const { data: endpointSignalsCounts } = useSignalCounts({
    targetId,
    targetType,
    endpointId,
    filters: { lifecycleState: LIST_BADGE_LIFECYCLE_STATE },
    enabled: Boolean(endpointId && targetId && targetType),
  });
  const endpointSignalsCount = endpointSignalsCounts?.counts.ALL;
  const parentTabScopeId = useTabScope();
  const { selectedTab, setTab } = useEndpointTabs(endpointId || 'default');
  const endpointDetailPaneScopeId = React.useMemo(() => {
    if (!parentTabScopeId || !endpointId) {
      return undefined;
    }

    return `${parentTabScopeId}/subTab_${selectedTab}`;
  }, [parentTabScopeId, endpointId, selectedTab]);
  // Set current endpoint ID for bottom panel context
  const { setCurrentEndpointId } = useBottomPanel();

  React.useEffect(() => {
    if (endpointId) {
      setCurrentEndpointId(endpointId);
    }

    return () => {
      setCurrentEndpointId(undefined);
    };
  }, [endpointId, setCurrentEndpointId]);
  const { closeTab, navigateInActiveTab } = useMainContentTabs();
  const { openContextMenu } = useGlobalContextMenu();
  const queryClient = useQueryClient();
  const invalidateEndpoints = useInvalidateEndpoints();
  const tRelatedAssets = useTranslations(
    'designSystem.applicationAssets.drawer',
  );
  // Delete dialog states
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const handleOpenRelatedWebpage = useCallback(
    (relatedAsset: { id: string; type: string; title?: string }) => {
      if (relatedAsset.type !== ASSET_TYPE_VALUES.WEBPAGE) {
        return;
      }

      navigateInActiveTab({
        id: `${ENTITY_TAB_TYPE.WEBPAGE}-${relatedAsset.id}`,
        title: relatedAsset.title || tRelatedAssets('webPage'),
        type: ENTITY_TAB_TYPE.WEBPAGE,
        data: { id: relatedAsset.id },
      });
    },
    [navigateInActiveTab, tRelatedAssets],
  );
  const handleRelatedWebpageContextMenu = useCallback(
    (event: CellContextMenuEvent) => {
      if (!event.data || !event.event) {
        return;
      }

      const webpage = event.data as {
        id: string;
        url: string;
      };

      openContextMenu({
        event: event.event as MouseEvent,
        items: [
          {
            type: GlobalMenuItemsTypeEnum.OPEN_IN_NEW_TAB,
            tab: {
              id: `${ENTITY_TAB_TYPE.WEBPAGE}-${webpage.id}`,
              title: webpage.url,
              type: ENTITY_TAB_TYPE.WEBPAGE,
              data: { id: webpage.id },
            },
          },
        ],
      });
    },
    [openContextMenu],
  );
  const handleDeleteClick = useCallback(() => {
    setIsDeleteDialogOpen(true);
  }, []);
  const handleConfirmDelete = useCallback(async () => {
    if (!endpointId) return;
    setIsDeleting(true);

    try {
      await terraProxyApiClient.deleteEndpoint(endpointId);
      toasterApi.success({
        title: 'Asset deleted',
        description: 'The endpoint has been deleted successfully.',
      });
      // Invalidate asset-related queries so lists refresh
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['assets-unified'],
          exact: false,
        }),
        queryClient.invalidateQueries({
          queryKey: ['asset-counts'],
          exact: false,
        }),
        invalidateEndpoints(),
        queryClient.invalidateQueries({
          queryKey: ['endpoints-preview'],
          exact: false,
        }),
      ]);
      setIsDeleteDialogOpen(false);
      closeTab(tab.id);
    } catch (error) {
      console.error('Failed to delete endpoint:', error);
      toasterApi.error({
        title: 'Failed to delete asset',
        description: 'There was an error deleting the endpoint.',
      });
    } finally {
      setIsDeleting(false);
    }
  }, [endpointId, closeTab, tab.id, queryClient, invalidateEndpoints]);
  // Use real endpoint data - no fallbacks
  const {
    data: realEndpointData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['endpoint-details-v2', endpointId],
    queryFn: async () => {
      if (!endpointId) {
        throw new Error('No valid endpoint ID found');
      }

      const result = await terraProxyApiClient.getEndpointById(endpointId);

      return result;
    },
    enabled: !!endpointId,
    refetchOnWindowFocus: false,
    staleTime: 30000,
  });
  // Create endpoint display data for header and other tabs
  const endpointData = React.useMemo((): EndpointDisplayData | null => {
    if (!realEndpointData) return null;
    // Type the endpoint data (already EndpointReturnDto from API)
    const typedEndpoint = realEndpointData as {
      id: string;
      url: string;
      path: string;
      description?: string | null;
      credentialsIds?: string[];
      credentialRoles?: string[];
      sources?: string[];
      request?: {
        method?: string;
      };
      responses?: Array<{
        status?: number;
      }>;
    };

    return {
      method: typedEndpoint.request?.method || 'GET',
      path: typedEndpoint.path,
      url: typedEndpoint.url,
      description: typedEndpoint.description || null,
      credentialsIds: typedEndpoint.credentialsIds,
      credentialRoles: typedEndpoint.credentialRoles,
      sources: typedEndpoint.sources,
      status:
        typedEndpoint.responses?.[0]?.status ||
        (typedEndpoint.request?.method === 'POST' ? 201 : 200),
      lifecycle: realEndpointData.lifecycle,
    };
  }, [realEndpointData]);
  const endpointCredentialLabels = React.useMemo(() => {
    if (!endpointData) {
      return [];
    }

    const credentialLabelsFromIds = (endpointData.credentialsIds || []).flatMap(
      (credentialId) => {
        const credential = credentialsById.get(credentialId);

        if (!credential) {
          return [];
        }

        const username = credential.username?.trim() || 'No Username';
        const role = credential.role?.trim() || 'Unknown Role';

        return [`Username: ${username} • Role: ${role}`];
      },
    );
    const hasAnonymousRole = (endpointData.credentialRoles || []).some(
      (credentialRole) => credentialRole === 'anonymous',
    );
    const fallbackLabels =
      credentialLabelsFromIds.length > 0
        ? []
        : (endpointData.credentialRoles || []).map(
            (credentialRole) => `Role: ${credentialRole}`,
          );
    const hasAnonymousLabelInFallback = fallbackLabels.some(
      (label) => label === 'Role: anonymous',
    );

    return [
      ...credentialLabelsFromIds,
      ...fallbackLabels,
      ...(hasAnonymousRole &&
      !credentialLabelsFromIds.some((label) =>
        label.endsWith('• Role: anonymous'),
      ) &&
      !hasAnonymousLabelInFallback
        ? ['Role: anonymous']
        : []),
    ];
  }, [endpointData, credentialsById]);
  const endpointSourceLabels = React.useMemo(() => {
    if (!endpointData?.sources?.length) {
      return [];
    }

    return [...new Set(endpointData.sources)].map((source) => {
      const translationKey =
        ENDPOINT_SOURCE_TRANSLATION_KEY_BY_VALUE[source as EndpointSourceType];

      if (translationKey) {
        return tEndpointDetails(`sourceValues.${translationKey}`);
      }

      return tEndpointDetails('sourceValues.manual');
    });
  }, [endpointData, tEndpointDetails]);
  const handleSendToCopilot = useCallback(() => {
    if (!endpointId || !endpointData) return;
    const label = `${endpointData.method} ${endpointData.path}`;

    addMention({
      id: `endpoint-${endpointId}`,
      label,
      icon: <TerminalIcon size={12} />,
      type: 'child',
      mentionEntity: {
        entityType: 'EndPoint',
        entityId: endpointId,
        label,
        extraData: {
          endpoint: {
            method: endpointData.method,
            path: endpointData.path,
            url: endpointData.url,
            description: endpointData.description,
          },
        },
      },
    });
  }, [endpointId, endpointData, addMention]);

  // Show loading state
  if (isLoading) {
    return (
      <Box
        h="full"
        w="full"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <VStack gap="4">
          <BodyDefault color="fg.muted">Loading endpoint data...</BodyDefault>
        </VStack>
      </Box>
    );
  }

  // Show error or missing data state
  if (error || !endpointId || !endpointData) {
    return (
      <Box
        h="full"
        w="full"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <VStack gap="6" maxW="md" textAlign="center">
          <VStack gap="2">
            <HeadingItem color="fg.default">
              Endpoint Data Not Available
            </HeadingItem>
            <BodyDefault color="fg.muted">
              {!endpointId && 'No valid endpoint ID found in tab data'}
              {endpointId &&
                error &&
                `Failed to load endpoint data: ${error.message}`}
              {endpointId &&
                !error &&
                !realEndpointData &&
                'No endpoint data returned from API'}
            </BodyDefault>
          </VStack>

          <VStack gap="2" w="full">
            <BodyStrong color="fg.muted">Tab Information:</BodyStrong>
            <Box p="4" bg="bg.subtle" borderRadius="md" w="full">
              <VStack gap="2" fontSize="sm">
                <HStack justify="space-between" w="full">
                  <MetaDefault color="fg.muted">Tab ID:</MetaDefault>
                  <MonoDefault>{tab.id}</MonoDefault>
                </HStack>
                <HStack justify="space-between" w="full">
                  <MetaDefault color="fg.muted">
                    Extracted Endpoint ID:
                  </MetaDefault>
                  <MonoDefault>{endpointId || 'None'}</MonoDefault>
                </HStack>
                <HStack justify="space-between" w="full">
                  <MetaDefault color="fg.muted">Method:</MetaDefault>
                  <BadgeHttp method={basicInfo.method} size="sm">
                    {basicInfo.method}
                  </BadgeHttp>
                </HStack>
                <HStack justify="space-between" w="full">
                  <MetaDefault color="fg.muted">Path:</MetaDefault>
                  <MonoDefault>{basicInfo.path}</MonoDefault>
                </HStack>
              </VStack>
            </Box>
          </VStack>

          <BodyDefault color="fg.muted">
            Please ensure the endpoint exists in the database and try again.
          </BodyDefault>
        </VStack>
      </Box>
    );
  }

  return (
    <Box h="full" w="full" overflow="hidden">
      <VStack gap="0" h="full" alignItems="stretch">
        {/* Header */}
        <Box p="4" borderBottomWidth="1px" borderColor="border.subtle">
          <VStack gap="2" alignItems="flex-start" w="full">
            <HStack gap="3" alignItems="center" w="full">
              <Box
                display="flex"
                alignItems="center"
                flexShrink={0}
                aria-hidden
              >
                <TerminalIcon size={20} />
              </Box>
              <BadgeHttp method={endpointData.method} size="sm">
                {endpointData.method}
              </BadgeHttp>
              <MonoDefault color="fg.default" flex="1" truncate>
                {endpointData.path}
              </MonoDefault>
              <ArchivedLifecycleBadge lifecycle={endpointData.lifecycle} />
              <AssetDetailsHeaderMutations
                isArchived={isArchivedLifecycle(endpointData.lifecycle)}
                onDelete={handleDeleteClick}
                onSendToCopilot={handleSendToCopilot}
              />
              {endpointId && (
                <CopyIdButton
                  id={endpointId}
                  entityName={tAssetEntity('asset')}
                />
              )}
              <Badge variant="outline" size="sm">
                {endpointData.status}
              </Badge>
            </HStack>

            <EndpointFeatureTagBadges
              assetTags={
                (realEndpointData as EndpointReturnDto | undefined)?.assetTags
              }
            />

            {/* Description */}
            {endpointData.description && (
              <Box w="full">
                <BodyDefault color="fg.muted" maxW="full">
                  {endpointData.description}
                </BodyDefault>
              </Box>
            )}

            <HStack gap="1" alignItems="center" w="full">
              <Link02Icon size={14} />
              <MonoSmall color="fg.muted" truncate>
                {endpointData.url}
              </MonoSmall>
            </HStack>

            {/* Credentials */}
            {endpointCredentialLabels.length > 0 && (
              <HStack gap="2" alignItems="center" w="full" flexWrap="wrap">
                <MetaDefault color="fg.muted">Credentials:</MetaDefault>
                {endpointCredentialLabels.map((credentialLabel, index) => (
                  <Badge key={index} size="sm" variant="outline">
                    {credentialLabel}
                  </Badge>
                ))}
              </HStack>
            )}

            {endpointSourceLabels.length > 0 && (
              <HStack gap="2" alignItems="center" w="full" flexWrap="wrap">
                <MetaDefault color="fg.muted">
                  {tEndpointDetails('sources')}
                </MetaDefault>
                {endpointSourceLabels.map((sourceLabel, index) => (
                  <Badge key={index} size="sm" variant="outline">
                    {sourceLabel}
                  </Badge>
                ))}
              </HStack>
            )}
          </VStack>
        </Box>

        {/* Content - Tabbed Interface — nested scope so tab filter states stay isolated. */}
        <TabScopeProvider scopeId={endpointDetailPaneScopeId}>
          <Box flex="1" overflow="hidden">
            <Tabs.Root
              value={selectedTab}
              size="sm"
              w="full"
              h="full"
              display="flex"
              flexDirection="column"
              overflow="hidden"
              lazyMount
              unmountOnExit
            >
              <Tabs.List justifyContent="flex-start">
                <Tabs.Trigger
                  value="details"
                  fontSize="sm"
                  px="3"
                  py="2"
                  onClick={() => setTab('details')}
                >
                  <LabelDefault color="inherit">
                    {tEndpointDetails('tabs.details')}
                  </LabelDefault>
                </Tabs.Trigger>
                <Tabs.Trigger
                  value="requests"
                  fontSize="sm"
                  px="3"
                  py="2"
                  onClick={() => setTab('requests')}
                >
                  <LabelDefault color="inherit">
                    {tEndpointDetails('tabs.trackedRequests')}
                  </LabelDefault>
                </Tabs.Trigger>
                <Tabs.Trigger
                  value="signals"
                  fontSize="sm"
                  px="3"
                  py="2"
                  onClick={() => setTab('signals')}
                >
                  <HStack gap="1.5" alignItems="center">
                    <LabelDefault color="inherit">
                      {tLayout('signals')}
                    </LabelDefault>
                    {endpointSignalsCount === undefined ? null : (
                      <Badge size="sm" variant="outline">
                        {endpointSignalsCount.toLocaleString()}
                      </Badge>
                    )}
                  </HStack>
                </Tabs.Trigger>
                <Tabs.Trigger
                  value="findings"
                  fontSize="sm"
                  px="3"
                  py="2"
                  onClick={() => setTab('findings')}
                >
                  <LabelDefault color="inherit">
                    {tLayout('findings')}
                  </LabelDefault>
                </Tabs.Trigger>
                <Tabs.Trigger
                  value="executions"
                  fontSize="sm"
                  px="3"
                  py="2"
                  onClick={() => setTab('executions')}
                >
                  <LabelDefault color="inherit">
                    {tLayout('executions')}
                  </LabelDefault>
                </Tabs.Trigger>
                <Tabs.Trigger
                  value="code"
                  fontSize="sm"
                  px="3"
                  py="2"
                  onClick={() => setTab('code')}
                >
                  <LabelDefault color="inherit">{tLayout('code')}</LabelDefault>
                </Tabs.Trigger>
                <Tabs.Trigger
                  value="webpages"
                  fontSize="sm"
                  px="3"
                  py="2"
                  onClick={() => setTab('webpages')}
                >
                  <LabelDefault color="inherit">
                    {tRelatedAssets('tabs.webpages')}
                  </LabelDefault>
                </Tabs.Trigger>
                <Tabs.Trigger
                  value="memories"
                  fontSize="sm"
                  px="3"
                  py="2"
                  onClick={() => setTab('memories')}
                >
                  <LabelDefault color="inherit">
                    {tEndpointMemories('tabLabel')}
                  </LabelDefault>
                </Tabs.Trigger>
                <Tabs.Indicator />
              </Tabs.List>

              <Tabs.Content value="details" flex="1" overflow="hidden" pt="0">
                <EndpointDetailsContent
                  endpointData={realEndpointData as EndpointReturnDto}
                />
              </Tabs.Content>

              <Tabs.Content
                value="requests"
                w="full"
                h="full"
                overflow="hidden"
                pt={0}
              >
                <Box h="full" w="full">
                  <RequestsContent
                    tabId={tab.id}
                    endpointId={endpointId}
                    selectedEndpoint={{
                      method: endpointData.method,
                      path: endpointData.path,
                      domain: safeGetHostname(
                        endpointData.url,
                        endpointData.path,
                      ),
                    }}
                  />
                </Box>
              </Tabs.Content>

              <Tabs.Content value="signals" flex="1" overflow="hidden" pt={0}>
                <Box h="full" w="full" overflow="hidden">
                  <SignalsContent
                    endpointId={endpointId}
                    endpointUrl={endpointData.url}
                    filterScope="endpoint"
                    enableRejectedSignalsToggle
                    lifecycleStateOverride={
                      isArchivedLifecycle(endpointData.lifecycle)
                        ? 'all'
                        : undefined
                    }
                  />
                </Box>
              </Tabs.Content>

              <Tabs.Content value="findings" flex="1" overflow="hidden" pt={0}>
                <Box h="full" w="full" overflow="hidden">
                  <FindingsContent
                    endpointId={endpointId}
                    endpointUrl={endpointData.url}
                    lifecycleStateOverride={
                      isArchivedLifecycle(endpointData.lifecycle)
                        ? 'all'
                        : undefined
                    }
                  />
                </Box>
              </Tabs.Content>

              <Tabs.Content
                value="executions"
                flex="1"
                overflow="hidden"
                pt={0}
              >
                <Box h="full" w="full" overflow="hidden">
                  <TracesContent
                    endpointId={endpointId}
                    endpointUrl={endpointData.url}
                    lifecycleStateOverride={
                      isArchivedLifecycle(endpointData.lifecycle)
                        ? 'all'
                        : undefined
                    }
                  />
                </Box>
              </Tabs.Content>

              <Tabs.Content
                value="code"
                w="full"
                h="full"
                overflow="hidden"
                pt={0}
              >
                {endpointId && <CodeModule endpointId={endpointId} />}
              </Tabs.Content>

              <Tabs.Content
                value="webpages"
                w="full"
                h="full"
                overflow="hidden"
                pt={0}
              >
                <Scroll orientation="vertical" showScrollbar={true} flex="1">
                  <Box p={4}>
                    {endpointId && (
                      <EndpointsWebpagesTab
                        endpointId={endpointId}
                        onOpenRelatedAsset={handleOpenRelatedWebpage}
                        onRowContextMenu={handleRelatedWebpageContextMenu}
                      />
                    )}
                  </Box>
                </Scroll>
              </Tabs.Content>

              <Tabs.Content
                value="memories"
                w="full"
                h="full"
                overflow="hidden"
                pt={0}
              >
                <EndpointMemoriesContent
                  applicationId={appTargetId}
                  endpointId={endpointId}
                />
              </Tabs.Content>
            </Tabs.Root>
          </Box>
        </TabScopeProvider>
      </VStack>

      {/* Delete confirmation dialog */}
      <ConfirmationDialog
        isOpen={isDeleteDialogOpen}
        onOpenChange={({ open }) => {
          setIsDeleteDialogOpen(open);
        }}
        onConfirm={handleConfirmDelete}
        loading={isDeleting}
        variant="destructive"
        title="Delete Asset"
        description="Are you sure you want to delete this endpoint? This action cannot be undone."
      />
    </Box>
  );
}
